import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  CountryCode,
} from "libphonenumber-js";
import { CountryOption, PhoneNumberData } from "./types";
import { PHONE_FORMATTING_PATTERNS } from "./constants";

export const findCountryByCode = (
  code: string,
  transformedCountryOptions: CountryOption[]
): CountryOption | null => {
  return (
    transformedCountryOptions.find(
      (country) =>
        country.value.toLowerCase() === code.toLowerCase() ||
        country.flag.toLowerCase() === code.toLowerCase()
    ) || null
  );
};

export const findCountryByDialCode = (
  dialCode: string,
  transformedCountryOptions: CountryOption[]
): CountryOption | null => {
  return transformedCountryOptions.find(
    (country) => country.dialCode.toLowerCase() === dialCode.toLowerCase()
  ) || null;
};

export const formatPhoneNumberFallback = (
  digits: string,
  country: CountryOption
): string => {
  if (!digits) return digits;

  for (const pattern of Object.values(PHONE_FORMATTING_PATTERNS)) {
    if ((pattern.countries as readonly string[]).includes(country.flag)) {
      return pattern.pattern(digits);
    }
  }

  return PHONE_FORMATTING_PATTERNS.DEFAULT.pattern(digits);
};

export const formatPhoneNumber = (
  phone: string,
  country: CountryOption | null
): string => {
  if (!phone || !country) return phone;

  const digits = phone.replace(/\D/g, "");
  if (!digits) return "";

  try {
    const countryCode = country.value.toLowerCase() as CountryCode;

    let phoneNumber = parsePhoneNumberFromString(digits, countryCode);

    if (!phoneNumber) {
      const withDialCode = `${country.dialCode}${digits}`;
      phoneNumber = parsePhoneNumberFromString(withDialCode);
    }

    if (phoneNumber && phoneNumber.country === countryCode.toUpperCase()) {
      let formatted = phoneNumber.formatNational();

      formatted = formatted
        .replace(new RegExp(`^\\${country.dialCode}\\s*`), "")
        .trim();

      if (
        formatted.startsWith("0") &&
        digits.length > 0 &&
        !digits.startsWith("0")
      ) {
        formatted = formatted.substring(1);
      }

      return formatted;
    }
  } catch (error) {
    console.warn("Phone formatting error:", error);
  }

  return formatPhoneNumberFallback(digits, country);
};

export const validatePhoneNumber = (
  phone: string,
  country: CountryOption | null
): PhoneNumberData => {
  if (!country) {
    return {
      countryCode: "",
      phoneNumber: phone,
      fullNumber: phone,
      isValid: false,
    };
  }

  if (!phone) {
    return {
      countryCode: country.value,
      phoneNumber: "",
      fullNumber: "",
      isValid: false,
    };
  }

  try {
    const countryCode = country.value.toLowerCase() as CountryCode;
    const digitsOnly = phone.replace(/\D/g, "");

    let phoneNumber = parsePhoneNumberFromString(digitsOnly, countryCode);

    if (!phoneNumber) {
      const fullNumber = `${country.dialCode}${digitsOnly}`;
      phoneNumber = parsePhoneNumberFromString(fullNumber);
    }

    if (phoneNumber) {
      const isValid = phoneNumber.isValid();

      return {
        countryCode: country.value,
        phoneNumber: digitsOnly,
        fullNumber: phoneNumber.number,
        nationalNumber: phoneNumber.nationalNumber,
        internationalNumber: phoneNumber.formatInternational(),
        isValid: isValid,
      };
    }
  } catch (error) {
    console.warn("Phone validation error:", error);
  }

  const digitsOnly = phone.replace(/\D/g, "");
  const countryCode = country.value.toLowerCase() as CountryCode;
  const isValid = isValidPhoneNumber(digitsOnly, countryCode);

  return {
    countryCode: country.value,
    phoneNumber: digitsOnly,
    fullNumber: `${country.dialCode}${digitsOnly}`,
    isValid,
  };
};

export const transformCountryOptions = (
  countryOptions: { name: string; code: string; flag: string }[]
): CountryOption[] => {
  return countryOptions.map((country) => ({
    label: `${country.name} (${country.code})`,
    value: country.flag.toUpperCase(),
    flag: country.flag,
    dialCode: country.code,
    name: country.name,
  }));
};

export const sortCountryOptions = (
  countryOptions: CountryOption[],
  preferredCountries: string[] = ["US", "GB", "CA", "AU"]
): CountryOption[] => {
  return countryOptions.sort((a, b) => {
    const aIsPreferred = preferredCountries.includes(a.flag);
    const bIsPreferred = preferredCountries.includes(b.flag);

    if (aIsPreferred && !bIsPreferred) return -1;
    if (!aIsPreferred && bIsPreferred) return 1;

    return a.name.localeCompare(b.name);
  });
};

export const isEmptyPhoneNumber = (phoneNumber: string | null | undefined): boolean => {
  return !phoneNumber || phoneNumber?.trim() === "";
};